import { useState } from 'react';
import { useList } from '@refinedev/core';
import { Bento, BentoCell } from '../../../components/Bento.js';
import { DataTable, type Column } from '../../../components/DataTable.js';

/**
 * Who is in the owner's units.
 *
 * `tenants_owner_select` joins through `lease_tenants` and `leases` to
 * `app_owner_can_see_unit()`, so this list is people on a lease for a unit in a
 * property held today — a legitimate owner question, answered by the same
 * `/api/tenants` the staff console calls.
 *
 * What is deliberately absent is the reason this screen is safe to build at all:
 * there is no policy admitting an owner to `tenant_sensitive`. Not a column allowlist
 * on this page — an absent grant. Widening the owner policy on `tenants` by accident
 * would still not reach an SSN, because reaching one would require a policy that does
 * not exist.
 *
 * The Property column is a client-side join against `/api/leases` rather than
 * a new backend field: `leases_owner_select` already scopes that list the
 * same way, and `LeaseDto.tenants[].tenantId` is enough to build a
 * tenantId → property/unit lookup with no new endpoint. Every lease the
 * tenant is named on is shown, not just an active one — `leaseCount` above
 * already counts ended terms the same way, and a resident who moved between
 * two of this owner's units should show both.
 */
interface TenantRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  leaseCount: number;
}

interface LeaseLookupRow {
  id: string;
  unitLabel: string;
  propertyName: string;
  tenants: { tenantId: string }[];
}

const columns = (propertiesFor: (tenantId: string) => string[]): Column<TenantRow>[] => [
  {
    key: 'name',
    header: 'Resident',
    render: (row) => (
      <>
        <strong>
          {row.lastName}, {row.firstName}
        </strong>
        <div className="muted">{row.email}</div>
      </>
    ),
  },
  {
    key: 'property',
    header: 'Property',
    render: (row) => {
      const labels = propertiesFor(row.id);
      return labels.length > 0 ? labels.join(', ') : '—';
    },
  },
  { key: 'phone', header: 'Phone', render: (row) => row.phone ?? '—' },
  { key: 'leases', header: 'Leases', numeric: true, render: (row) => row.leaseCount },
];

export function OwnerResidents() {
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { data, isLoading, error } = useList<TenantRow>({
    resource: 'tenants',
    pagination: { current: page, pageSize },
  });

  // Unfiltered, same reasoning as everywhere else on this portal — RLS
  // already scopes this to leases in units the owner currently holds.
  const { data: leases } = useList<LeaseLookupRow>({
    resource: 'leases',
    pagination: { mode: 'off' },
  });

  const propertiesFor = (tenantId: string): string[] =>
    (leases?.data ?? [])
      .filter((lease) => lease.tenants.some((t) => t.tenantId === tenantId))
      .map((lease) => `${lease.propertyName} — ${lease.unitLabel}`);

  return (
    <section>
      <h1>Residents</h1>
      <p className="muted">
        Leaseholders and tenants in any unit or property you currently hold are displayed.
      </p>

      <Bento>
        <BentoCell span="full">
          <DataTable
            columns={columns(propertiesFor)}
            rows={data?.data ?? []}
            rowKey={(row) => row.id}
            isLoading={isLoading}
            error={error}
            emptyMessage="No residents in your units yet."
            pagination={{ page, pageSize, total: data?.total ?? 0, onPageChange: setPage }}
          />
        </BentoCell>

        <BentoCell span="full" title="Contacting a resident">
          <p className="bento__hint">Please route any communication through the office.</p>
        </BentoCell>
      </Bento>
    </section>
  );
}
